import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useKeyboard } from '../hooks/useKeyboard';

interface DangerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmButtonText: string;
  requiresConfirmationText?: boolean;
  confirmationText?: string;
  children?: React.ReactNode;
}

export const DangerModal: React.FC<DangerModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText,
  requiresConfirmationText = false,
  confirmationText = 'DELETE',
  children
}) => {
  const [inputText, setInputText] = useState('');
  const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null);

  const handleConfirm = () => {
    if (!requiresConfirmationText || inputText === confirmationText) {
      onConfirm();
      setInputText(''); // Reset input
    }
  };

  const handleClose = () => {
    setInputText(''); // Reset input
    onClose();
  };

  const handleModalMouseDown = (e: React.MouseEvent) => {
    setMouseDownTarget(e.target);
  };

  const handleModalClick = (e: React.MouseEvent) => {
    // Only close modal if mousedown and mouseup happened on the same overlay target
    if (e.target === mouseDownTarget && e.target === e.currentTarget) {
      handleClose();
    }
    setMouseDownTarget(null);
  };

  const isConfirmEnabled = !requiresConfirmationText || inputText === confirmationText;

  // Global keyboard handling for modal
  useKeyboard({
    onEscape: handleClose,
    onEnter: isConfirmEnabled ? handleConfirm : undefined,
    enabled: isOpen,
    enableEnter: isConfirmEnabled
  });

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="modal-overlay" 
        onMouseDown={handleModalMouseDown}
        onClick={handleModalClick}
      >
        <div 
          className="modal-content danger-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <AlertTriangle size={24} color="#ff4444" />
            <h3>{title}</h3>
            <button 
              className="modal-close-btn"
              onClick={handleClose}
              aria-label="Close modal"
            >
              <X size={18} />
            </button>
          </div>
          <div className="modal-body">
            <p>{message}</p>
            {children}
            
            {requiresConfirmationText && (
              <div className="confirmation-input-section">
                <p className="confirmation-instruction">
                  Type <strong>{confirmationText}</strong> to confirm this action:
                </p>
                <input
                  type="text"
                  className="confirmation-input"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder={`Type "${confirmationText}" here`}
                  autoFocus
                />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={handleClose}>
              Cancel
            </button>
            <button 
              className={`btn btn-danger ${!isConfirmEnabled ? 'btn-disabled' : ''}`}
              onClick={handleConfirm}
              disabled={!isConfirmEnabled}
            >
              {confirmButtonText}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--bg-overlay);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: var(--bg-primary);
          border-radius: var(--radius-large);
          box-shadow: var(--shadow-heavy);
          max-height: 90vh;
          overflow: hidden;
          border: 1px solid var(--border-secondary);
          animation: modalSlideIn 0.2s ease-out;
          padding: 0;
        }

        @keyframes modalSlideIn {
          0% { transform: scale(0.9); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }

        .danger-modal {
          max-width: 500px;
          width: 90%;
          border: 2px solid #dc2626;
          box-shadow: 0 20px 60px rgba(220, 38, 38, 0.3);
        }

        .danger-modal .modal-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 24px;
          margin: 0 -24px;
          border-bottom: 1px solid #dc2626;
          background: rgba(220, 38, 38, 0.05);
          position: relative;
          border-top-left-radius: var(--radius-large);
          border-top-right-radius: var(--radius-large);
        }

        .danger-modal .modal-header h3 {
          margin: 0;
          color: #dc2626;
          font-size: 18px;
          font-weight: 600;
          flex: 1;
        }

        .modal-close-btn {
          background: var(--bg-secondary);
          border: none;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: var(--border-primary);
        }

        .danger-modal .modal-body {
          padding: 24px;
        }

        .danger-modal .modal-body p {
          margin-bottom: 16px;
          color: var(--text-primary);
          line-height: 1.5;
        }

        .confirmation-input-section {
          margin-top: 20px;
        }

        .confirmation-instruction {
          margin-bottom: 12px !important;
          color: var(--text-secondary) !important;
          font-size: 14px;
        }

        .confirmation-input {
          width: 100%;
          padding: 10px 12px;
          border: 2px solid var(--border-secondary);
          border-radius: var(--radius-small);
          background: var(--bg-secondary);
          color: var(--text-primary);
          font-family: 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .confirmation-input:focus {
          outline: none;
          border-color: #dc2626;
          background: var(--bg-primary);
        }

        .danger-modal .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 24px;
          margin: 0 -24px;
          border-top: 1px solid #dc2626;
          background: rgba(220, 38, 38, 0.05);
          border-bottom-left-radius: var(--radius-large);
          border-bottom-right-radius: var(--radius-large);
        }

        .btn {
          padding: 10px 16px;
          border-radius: var(--radius-small);
          border: none;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
        }

        .btn-secondary {
          background: var(--bg-primary);
          color: var(--text-primary);
          border: 1px solid var(--border-primary);
        }

        .btn-secondary:hover {
          background: var(--border-secondary);
        }

        .btn-danger {
          background: #dc2626;
          color: white;
        }

        .btn-danger:hover:not(.btn-disabled) {
          background: #b91c1c;
        }

        .btn-disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
};